import { useNavigate } from 'react-router-dom';
import { useEffect, useState, type MouseEvent } from 'react';
import { api, type Artifact } from '../api/client';
import { useMembers } from '../hooks/useApi';
import { Avatar } from './AppShell';
import ArtifactActionsMenu from './ArtifactActionsMenu';
import ShareDialog from './ShareDialog';
import { relativeTime } from '../utils';


const getFileType = (contentType?: string): string => {
  if (!contentType) return 'HTML';
  if (contentType.includes('pdf')) return 'PDF';
  if (contentType.includes('markdown')) return 'MD';
  return 'HTML';
};

const fileTypeBadgeStyles: Record<string, { background: string; color: string }> = {
  HTML: {
    background: 'var(--rd-badge-html-bg)',
    color: 'var(--rd-badge-html-text)',
  },
  MD: {
    background: 'var(--rd-badge-md-bg)',
    color: 'var(--rd-badge-md-text)',
  },
  PDF: {
    background: 'var(--rd-badge-pdf-bg)',
    color: 'var(--rd-badge-pdf-text)',
  },
};

interface ArtifactRowProps {
  artifact: Artifact;
  onStarChanged: () => void;
}

export default function ArtifactRow({ artifact, onStarChanged }: ArtifactRowProps) {
  const navigate = useNavigate();
  const [starred, setStarred] = useState(artifact.starred);
  const [starring, setStarring] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const { members } = useMembers();
  const fileType = getFileType((artifact as unknown as Record<string, unknown>).content_type as string | undefined);

  useEffect(() => {
    setStarred(artifact.starred);
  }, [artifact.starred]);

  const handleToggleStar = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (starring) return;

    const nextStarred = !starred;
    setStarred(nextStarred);
    setStarring(true);
    try {
      if (nextStarred) {
        await api.starArtifact(artifact.id);
      } else {
        await api.unstarArtifact(artifact.id);
      }
      onStarChanged();
    } catch {
      setStarred(!nextStarred);
    } finally {
      setStarring(false);
    }
  };

  return (
    <div
      role="listitem"
      tabIndex={0}
      className="group flex items-center gap-[16px] cursor-pointer transition-colors duration-[120ms] rounded-[10px]"
      style={{
        padding: '14px 12px',
      }}
      onClick={() => navigate(`/a/${artifact.id}`)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') navigate(`/a/${artifact.id}`);
      }}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = 'var(--rd-surface-hover)')
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.background = 'transparent')
      }
    >
      {/* Title + badge, description below */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-[6px]">
          <span
            className="text-[14px] font-semibold truncate"
            style={{ color: 'var(--rd-text-strong)' }}
          >
            {artifact.title}
          </span>
          <span
            className="inline-flex items-center h-[18px] px-[var(--sp-1)] rounded-[var(--radius-sm)] text-[11px] font-semibold flex-shrink-0"
            style={fileTypeBadgeStyles[fileType]}
          >
            {fileType}
          </span>
          {artifact.status === 'archived' && (
            <span
              className="inline-flex items-center h-[18px] px-[var(--sp-1)] rounded-[var(--radius-sm)] text-[11px] font-semibold flex-shrink-0"
              style={{
                background: 'var(--color-surface-artifact-archived)',
                color: 'var(--color-text-artifact-archived)',
              }}
            >
              Archived
            </span>
          )}
        </div>
        {artifact.description && (
          <div
            className="text-[13px] truncate pr-[100px]"
            style={{ color: 'var(--rd-text-subtle)' }}
          >
            {artifact.description}
          </div>
        )}
      </div>

      {/* Right meta: star toggle, avatar+name, time */}
      <div className="flex items-center gap-0 flex-shrink-0">
        {/* Star toggle button */}
        <button
          onClick={handleToggleStar}
          disabled={starring}
          className="w-[36px] h-[36px] rounded-[var(--radius-md)] border-0 bg-transparent cursor-pointer flex items-center justify-center transition-colors duration-[120ms] disabled:opacity-50"
          style={{
            color: starred ? 'var(--rd-star)' : 'var(--rd-star-inactive)',
          }}
          title={starred ? 'Remove star' : 'Star artifact'}
          aria-label={starred ? 'Remove star' : 'Star artifact'}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill={starred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round">
            <path d="M8 1.5L10 5.8L14.7 6.4L11.2 9.6L12.1 14.2L8 11.9L3.9 14.2L4.8 9.6L1.3 6.4L6 5.8L8 1.5Z" />
          </svg>
        </button>

        {/* Author avatar + name */}
        <div className="flex items-center gap-[6px]" style={{ width: 140 }}>
          <Avatar name={artifact.author.name} avatarUrl={artifact.author.avatar_url} size={22} />
          <span
            className="text-[13px] font-medium truncate hidden md:inline"
            style={{ color: 'var(--rd-avatar-default)' }}
          >
            {artifact.author.name}
          </span>
        </div>

        {/* Time */}
        <span
          className="text-[13px] hidden sm:inline text-right"
          style={{ color: 'var(--rd-text-meta)', width: 48, flexShrink: 0 }}
        >
          {relativeTime(artifact.updated_at)}
        </span>

        {/* Actions menu — visible on row hover */}
        <div className="ml-[4px] flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-[120ms]">
          <ArtifactActionsMenu artifact={artifact} onChanged={onStarChanged} onShare={() => setShareOpen(true)} canArchive />
        </div>
      </div>

      <ShareDialog
        artifactId={artifact.id}
        open={shareOpen}
        members={members}
        artifactAuthor={artifact.author}
        onClose={() => setShareOpen(false)}
      />
    </div>
  );
}
